import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { masterDataApi } from "@/services/masterDataApi";
import { promptLibraryApi } from "@/services/promptLibraryApi";

export function useTherapyAreas() {
  return useQuery({ queryKey: ["master", "therapy-areas"], queryFn: masterDataApi.therapyAreas });
}

export function useNetsuiteClients(search: string) {
  return useQuery({
    queryKey: ["master", "netsuite-clients", search],
    queryFn: () => masterDataApi.netsuiteClients(search),
    enabled: search.trim().length > 0,
    staleTime: 30_000, // cache each search result for 30 s
    placeholderData: (prev) => prev, // keep previous results while new search loads
  });
}

export function useNetsuiteClientById(id: string | null) {
  return useQuery({
    queryKey: ["master", "netsuite-clients", "by-id", id],
    queryFn: () => masterDataApi.netsuiteClientById(id!),
    enabled: !!id,
    staleTime: 300_000,
  });
}


export function useKantataProjects(search: string) {
  // Same limit whether browsing the initial (blank-search) list or actively searching
  const limit = 50;
  return useQuery({
    queryKey: ["master", "kantata-projects", search.trim(), limit],
    queryFn: () => masterDataApi.kantataProjects(search, limit),
    staleTime: 30_000, // cache each search result for 30 s
    placeholderData: (prev) => prev, // keep previous results while new search loads
  });
}

export function useKantataProjectById(id: string | null) {
  return useQuery({
    queryKey: ["master", "kantata-projects", "by-id", id],
    queryFn: () => masterDataApi.kantataProjectById(id!),
    enabled: !!id,
    staleTime: 300_000,
  });
}

export function useEvidenceTypes() {
  return useQuery({ queryKey: ["master", "evidence-types"], queryFn: masterDataApi.evidenceTypes });
}

export function useSourceTypes() {
  return useQuery({ queryKey: ["master", "source-types"], queryFn: masterDataApi.sourceTypes });
}

// Admin list hooks (all records including inactive)
export function useAdminTherapyAreas() {
  return useQuery({ queryKey: ["admin", "therapy-areas"], queryFn: masterDataApi.adminTherapyAreas });
}

// Admin mutations — shared factory to avoid duplication
function useMasterMutations(
  queryKeys: string[][],
  createFn: (name: string) => Promise<unknown>,
  updateFn: (id: string, data: Record<string, unknown>) => Promise<unknown>,
  deleteFn: (id: string) => Promise<void>,
) {
  const qc = useQueryClient();
  const invalidate = () => queryKeys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));

  const create = useMutation({ mutationFn: createFn, onSuccess: invalidate });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => updateFn(id, data),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: deleteFn, onSuccess: invalidate });

  return { create, update, remove };
}

export function useTherapyAreaAdmin() {
  return useMasterMutations(
    [["master", "therapy-areas"], ["admin", "therapy-areas"]],
    masterDataApi.createTherapyArea,
    masterDataApi.updateTherapyArea,
    masterDataApi.deleteTherapyArea,
  );
}

export function useEvidenceTypeAdmin() {
  return useMasterMutations(
    [["master", "evidence-types"]],
    masterDataApi.createEvidenceType,
    masterDataApi.updateEvidenceType,
    masterDataApi.deleteEvidenceType,
  );
}

export function useSourceTypeAdmin() {
  return useMasterMutations(
    [["master", "source-types"]],
    masterDataApi.createSourceType,
    masterDataApi.updateSourceType,
    masterDataApi.deleteSourceType,
  );
}

export function useAdminTemplateTypes() {
  return useQuery({ queryKey: ["admin", "template-types"], queryFn: promptLibraryApi.getAdminTemplateTypes });
}

export function useAdminTemplateFormatTypes() {
  return useQuery({ queryKey: ["admin", "template-format-types"], queryFn: promptLibraryApi.getAdminTemplateFormatTypes });
}

export function useTemplateFormatTypeAdmin() {
  return useMasterMutations(
    [["admin", "template-format-types"]],
    promptLibraryApi.createTemplateFormatType,
    (id: string, data: Record<string, unknown>) =>
      promptLibraryApi.updateTemplateFormatType(Number(id), data as Partial<{ name: string; is_active: boolean }>),
    (id: string) => promptLibraryApi.deleteTemplateFormatType(Number(id)),
  );
}

export function useTemplateTypeAdmin() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "template-types"] });

  const create = useMutation({
    mutationFn: (data: { name: string; is_active?: boolean; settings?: string | null }) =>
      promptLibraryApi.createTemplateType(data),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ name: string; is_active: boolean; settings: string | null }> }) =>
      promptLibraryApi.updateTemplateType(id, data),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => promptLibraryApi.deleteTemplateType(id),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

export function useAdminApiConfigurations() {
  return useQuery({ queryKey: ["admin", "api-configurations"], queryFn: masterDataApi.adminApiConfigurations });
}

export function useApiConfigurationAdmin() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "api-configurations"] });

  const create = useMutation({
    mutationFn: (data: Parameters<typeof masterDataApi.createApiConfiguration>[0]) =>
      masterDataApi.createApiConfiguration(data),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof masterDataApi.updateApiConfiguration>[1] }) =>
      masterDataApi.updateApiConfiguration(id, data),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => masterDataApi.deleteApiConfiguration(id),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
